import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/shared/providers/AuthProvider";
import { apiRoutes } from "@/shared/api/routes";
import type {
  UserPreferences,
  UserPreferencesApiResponse,
  UserPreferencesState,
  UserPreferenceItem,
  ProfilePreferenceValue,
} from "@/shared/types/preferences.types";

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

interface UserPreferencesContextType extends UserPreferencesState {
  /** Re-fetch preferences from the server */
  refetchPreferences: () => Promise<void>;
}

const UserPreferencesContext = createContext<
  UserPreferencesContextType | undefined
>(undefined);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Safely parse a preference value.
 * The API returns `value` as a JSON-encoded string, so we JSON.parse it.
 * If it is already an object (or not parseable) we return it as-is / null.
 */
const parsePreferenceValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

const normalizePreferences = (items: UserPreferenceItem[]): UserPreferences => {
  const profileItem = items.find((item) => item.key === "profile");

  let profileValue: ProfilePreferenceValue | null = null;
  if (profileItem) {
    const parsed = parsePreferenceValue(profileItem.value);
    if (parsed !== null && typeof parsed === "object") {
      profileValue = parsed as ProfilePreferenceValue;
    }
  }

  return {
    raw: items,
    profile: profileValue,
    getByKey: (key: string) => items.find((item) => item.key === key),
    getParsedValueByKey: (key: string) => {
      const item = items.find((i) => i.key === key);
      return item !== undefined ? parsePreferenceValue(item.value) : null;
    },
  };
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider: React.FC<
  UserPreferencesProviderProps
> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [state, setState] = useState<UserPreferencesState>({
    preferences: null,
    loading: false,
    error: null,
  });

  const fetchPreferences = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch(apiRoutes.proxy("session/me/preferences"), {
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Accept-Language": "en-US",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch preferences: ${response.status} ${response.statusText}`
        );
      }

      const json: UserPreferencesApiResponse = await response.json();
      const items: UserPreferenceItem[] = Array.isArray(json?.data)
        ? json.data
        : [];

      setState({
        preferences: normalizePreferences(items),
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({
        preferences: null,
        loading: false,
        error:
          err instanceof Error ? err.message : "Failed to load preferences",
      });
    }
  }, []);

  // Fetch once when the user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferences();
    } else {
      // Clear preferences when user logs out
      setState({ preferences: null, loading: false, error: null });
    }
  }, [isAuthenticated, fetchPreferences]);

  // Re-sync whenever ViewSettingsModal saves changes.
  // usePreferences (usePreferences.ts) calls queryClient.invalidateQueries({ queryKey: ['preferences', tenantId] })
  // on a successful update mutation — this causes a new 'success' event on that query.
  // We subscribe to the queryCache and re-fetch our raw preferences whenever that happens.
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === "updated" &&
        event.action?.type === "success" &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === "preferences"
      ) {
        fetchPreferences();
      }
    });

    return unsubscribe;
  }, [isAuthenticated, queryClient, fetchPreferences]);

  const contextValue = useMemo<UserPreferencesContextType>(
    () => ({
      ...state,
      refetchPreferences: fetchPreferences,
    }),
    [state, fetchPreferences]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useUserPreferences must be used within a UserPreferencesProvider"
    );
  }
  return context;
};